import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Banknote, Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const CAN_MANAGE = ["ORG_ADMIN", "ACCOUNTS"];

export default function VendorAdvances() {
  const { user } = useAuth();
  const canManage = CAN_MANAGE.includes(user?.role);
  const { companies } = useCompany() || {};
  const [advances, setAdvances] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [purchaseBills, setPurchaseBills] = useState([]);
  const [serviceBills, setServiceBills] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [advRes, vendorRes, pbRes, sbRes] = await Promise.all([
        api.get("/procurement/vendor-advances"), api.get("/procurement/vendors"),
        api.get("/procurement/purchase-bills").catch(() => ({ data: [] })), api.get("/procurement/service-bills").catch(() => ({ data: [] })),
      ]);
      setAdvances(advRes.data); setVendors(vendorRes.data);
      setPurchaseBills(pbRes.data); setServiceBills(sbRes.data);
      const [poRes, woRes] = await Promise.all([api.get("/procurement/purchase-orders").catch(() => ({ data: [] })), api.get("/procurement/work-orders").catch(() => ({ data: [] }))]);
      setOrders([
        ...(poRes.data || []).map((o) => ({ id: o.id, kind: "PO", field: "purchase_order_id", number: o.number, vendor_id: o.vendor_id, status: o.status, total: o.total_amount })),
        ...(woRes.data || []).map((o) => ({ id: o.id, kind: "WO", field: "work_order_id", number: o.number, vendor_id: o.vendor_id, status: o.status, total: o.total_amount })),
      ]);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || "—";

  const post = async (advance) => {
    setBusyId(advance.id);
    try {
      await api.post(`/procurement/vendor-advances/${advance.id}/post`);
      toast.success("Advance posted to GL");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="vendor-advances-page">
      <PageHeader title="Vendor Advances" description="Advance payments to vendors, ahead of a purchase bill." action={canManage && <CreateAdvanceDialog companies={companies} vendors={vendors} orders={orders} onCreated={load} />} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && advances.length === 0 ? (
          <EmptyState title="No vendor advances yet" testId="vendor-advances-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Vendor</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Used</TableHead><TableHead className="text-right">Balance</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {advances.map((a) => (
                <TableRow key={a.id} data-testid={`advance-row-${a.number}`}>
                  <TableCell className="font-mono text-xs text-foreground">{a.number}</TableCell>
                  <TableCell className="text-foreground">
                    {vendorName(a.vendor_id)}
                    {(a.purchase_order_id || a.work_order_id) && <div className="text-[10px] text-muted-foreground">against {orders.find((o) => o.id === (a.purchase_order_id || a.work_order_id))?.number || "order"}</div>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{a.advance_date}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{inrAmount(a.amount)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">{a.status === "POSTED" ? inrAmount(a.allocated_amount) : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{a.status === "POSTED" ? inrAmount(Number(a.amount) - Number(a.allocated_amount || 0)) : "—"}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <StatusPill value={a.status} />
                    {a.status === "POSTED" && <UseBadge amount={Number(a.amount)} used={Number(a.allocated_amount || 0)} />}
                  </TableCell>
                  <TableCell className="text-right">
                    {canManage && a.status === "POSTED" && Number(a.amount) - Number(a.allocated_amount || 0) > 0.005 && (
                      <UseAdvanceDialog advance={a} vendorName={vendorName(a.vendor_id)} purchaseBills={purchaseBills} serviceBills={serviceBills} orders={orders} onDone={load} />
                    )}
                    {canManage && a.status === "DRAFT" && (
                      <Button size="sm" variant="outline" disabled={busyId === a.id} onClick={() => post(a)} data-testid={`post-advance-button-${a.number}`}>
                        <Banknote className="h-3.5 w-3.5" /> Post
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateAdvanceDialog({ companies, vendors, orders = [], onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", vendor_id: "", advance_date: "", amount: "", order: "" });
  const [saving, setSaving] = useState(false);
  const vendorOrders = orders.filter((o) => o.vendor_id === form.vendor_id && o.status === "APPROVED");
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const order = orders.find((o) => o.id === form.order);
      const { order: _ignored, ...rest } = form;
      await api.post("/procurement/vendor-advances", { ...rest, purchase_order_id: order?.kind === "PO" ? order.id : null, work_order_id: order?.kind === "WO" ? order.id : null });
      toast.success("Vendor advance created");
      setForm({ company_id: "", vendor_id: "", advance_date: "", amount: "", order: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-advance-button"><Plus className="h-4 w-4" /> New Advance</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Vendor Advance</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-advance-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="advance-company-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vendor</Label>
              <Select required value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                <SelectTrigger data-testid="advance-vendor-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(vendors || []).map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Against order <span className="font-normal text-muted-foreground">(optional — the PO / work order this advance is for)</span></Label>
            <Select value={form.order || "none"} onValueChange={(v) => setForm({ ...form, order: v === "none" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="advance-order-select"><SelectValue placeholder={form.vendor_id ? "Select" : "Choose the vendor first"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not for a specific order</SelectItem>
                {vendorOrders.map((o) => <SelectItem key={o.id} value={o.id}>{o.kind} {o.number} · ₹{Number(o.total || 0).toLocaleString("en-IN")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input required type="date" data-testid="advance-date-input" value={form.advance_date} onChange={set("advance_date")} className="mt-1.5" /></div>
            <div><Label>Amount</Label><Input required type="number" step="0.01" data-testid="advance-amount-input" value={form.amount} onChange={set("amount")} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-advance-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const inrAmount = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function UseBadge({ amount, used }) {
  const left = amount - used;
  const [label, tone] =
    left <= 0.005
      ? ["Fully used", "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"]
      : used > 0
        ? ["Partly used", "bg-amber-500/10 text-amber-600 dark:text-amber-400"]
        : ["Not used yet", "bg-sky-500/10 text-sky-600 dark:text-sky-400"];
  return <span className={`ml-1.5 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}>{label}</span>;
}

function UseAdvanceDialog({ advance, vendorName, purchaseBills, serviceBills, orders = [], onDone }) {
  const [open, setOpen] = useState(false);
  const [billKey, setBillKey] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const balance = Math.round((Number(advance.amount) - Number(advance.allocated_amount || 0)) * 100) / 100;

  // This vendor's posted bills that still have something to pay
  const bills = [
    ...purchaseBills.filter((b) => b.vendor_id === advance.vendor_id && b.status === "POSTED")
      .map((b) => ({ key: `pb:${b.id}`, id: b.id, field: "purchase_bill_id", number: b.number, kind: "Purchase", date: b.bill_date, due: Number(b.total_amount), paid: Number(b.paid_amount || 0) + Number(b.adjusted_amount || 0), order: b.purchase_order_id })),
    ...serviceBills.filter((b) => b.vendor_id === advance.vendor_id && b.status === "POSTED")
      .map((b) => ({ key: `sb:${b.id}`, id: b.id, field: "service_bill_id", number: b.number, kind: "Service", date: b.bill_date, due: Number(b.net_payable), paid: Number(b.paid_amount || 0) + Number(b.adjusted_amount || 0), order: b.work_order_id })),
  ]
    .map((b) => ({ ...b, pending: Math.round((b.due - b.paid) * 100) / 100 }))
    .filter((b) => b.pending > 0)
    .map((b) => ({ ...b, ofOrder: Boolean(b.order) && b.order === (advance.purchase_order_id || advance.work_order_id) }))
    .sort((a, b) => Number(b.ofOrder) - Number(a.ofOrder) || String(a.date).localeCompare(String(b.date)));
  const bill = bills.find((b) => b.key === billKey);
  const max = bill ? Math.min(bill.pending, balance) : balance;

  const pick = (key) => {
    setBillKey(key);
    const b = bills.find((x) => x.key === key);
    if (b) setAmount(Math.min(b.pending, balance).toFixed(2));
  };

  const problem = !bill ? "" : Number(amount) <= 0 ? "Enter an amount" : Number(amount) > max + 0.005
    ? `Maximum is ₹${inrAmount(max)} (${bill.pending < balance ? "what the bill still owes" : "what's left of the advance"})` : "";

  const save = async (e) => {
    e.preventDefault();
    if (!bill || problem) return;
    setSaving(true);
    try {
      await api.post(`/procurement/vendor-advances/${advance.id}/allocate`, { [bill.field]: bill.id, amount, allocation_date: date });
      toast.success(`₹${inrAmount(amount)} of ${advance.number} used against ${bill.number}`);
      setOpen(false);
      setBillKey(""); setAmount("");
      onDone?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`use-advance-button-${advance.number}`}><Link2 className="h-3.5 w-3.5" /> Use against bill</Button>
      </DialogTrigger>
      <DialogContent className="overflow-x-hidden sm:max-w-lg">
        <DialogHeader><DialogTitle>Use advance {advance.number}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3.5">
          <p className="text-sm text-muted-foreground">{vendorName} · advance balance <span className="font-mono font-semibold text-foreground">₹{inrAmount(balance)}</span></p>
          {bills.length === 0 ? (
            <p className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">This vendor has no unpaid posted bills yet. Post the bill first, then come back to use the advance.</p>
          ) : (
            <>
              <div>
                <Label>Bill</Label>
                <Select value={billKey} onValueChange={pick}>
                  <SelectTrigger className="mt-1.5 min-w-0 [&>span]:truncate" data-testid="use-advance-bill-select"><SelectValue placeholder="Select a bill" /></SelectTrigger>
                  <SelectContent>
                    {bills.map((b) => <SelectItem key={b.key} value={b.key}>{b.number} · {b.kind} · ₹{inrAmount(b.pending)}{b.ofOrder ? " · this advance's order" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                {bill && <p className="mt-1 text-[11px] text-muted-foreground">{bill.kind} bill dated {bill.date} · pending ₹{inrAmount(bill.pending)}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount</Label><Input required type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5" data-testid="use-advance-amount-input" /></div>
                <div><Label>Date</Label><Input required type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" /></div>
              </div>
              {bill && !problem && Number(amount) > 0 && (
                <p className="text-xs text-muted-foreground">
                  After this: bill balance ₹{inrAmount(bill.pending - Number(amount))} · advance balance ₹{inrAmount(balance - Number(amount))}
                </p>
              )}
              {problem && <p className="rounded-lg bg-rose-500/10 p-2 text-xs text-rose-700 dark:text-rose-400">{problem}</p>}
            </>
          )}
          <DialogFooter>
            <Button type="submit" disabled={saving || !bill || Boolean(problem)} data-testid="use-advance-save-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Use advance</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
